/**
 * Discount Engine -- the admin page shell (phase 6b, Task 1).
 *
 * Layout: title, StatsBar (engine counters), MUI Tabs bound to the sub-route,
 * then the area. Registered in src/admin/index.tsx as `/discounts/*`; the
 * routes below are relative to that:
 *
 *   /discounts                 -> redirect to /discounts/promotions
 *   /discounts/promotions      PromotionsList     (Task 2)
 *   /discounts/promotions/:id  PromotionsDetail   (Task 3)
 *   /discounts/customers       CustomersList      (Task 4)
 *   /discounts/customers/:id   CustomersDetail    (Task 4)
 *   /discounts/loyalty         LoyaltyScreen      (Task 5)
 *   /discounts/referrals       ReferralsScreen    (Task 6)
 *
 * Each area is imported ONLY from its directory's index.ts, which Task 1
 * created as a stub and the area's task replaces -- so this file is frozen
 * while Tasks 2-6 run in parallel.
 */
import React from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { Title } from 'react-admin';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { StatsBar } from './StatsBar';
import { PromotionsList, PromotionsDetail } from './promotions';
import { CustomersList, CustomersDetail } from './customers';
import { LoyaltyScreen } from './loyalty';
import { ReferralsScreen } from './referrals';

export const DISCOUNTS_BASE = '/discounts';

type Area = 'promotions' | 'customers' | 'loyalty' | 'referrals';
const AREAS: Array<{ key: Area; label: string }> = [
  { key: 'promotions', label: 'Promotions' },
  { key: 'customers', label: 'Customers' },
  { key: 'loyalty', label: 'Loyalty' },
  { key: 'referrals', label: 'Referrals' },
];

/** The tab that owns a pathname: the first segment after /discounts (a detail route keeps its parent tab). */
export const areaOf = (pathname: string): Area => {
  const rest = pathname.startsWith(DISCOUNTS_BASE) ? pathname.slice(DISCOUNTS_BASE.length) : pathname;
  const seg = rest.split('/').filter(Boolean)[0];
  return (AREAS.find((a) => a.key === seg)?.key ?? 'promotions');
};

const DiscountsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const area = areaOf(location.pathname);

  return (
    <Box sx={{ p: 3 }}>
      <Title title="Discount Engine" />
      <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
        Discount Engine
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Promotions, codes, loyalty tiers and referral rewards — the native engine that replaces Voucherify.
      </Typography>

      <StatsBar />

      <Tabs
        value={area}
        onChange={(_e, next: Area) => navigate(`${DISCOUNTS_BASE}/${next}`)}
        aria-label="Discount engine areas"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        {AREAS.map((a) => (
          <Tab key={a.key} value={a.key} label={a.label} id={`discounts-tab-${a.key}`} />
        ))}
      </Tabs>

      <Routes>
        <Route index element={<Navigate to={`${DISCOUNTS_BASE}/promotions`} replace />} />
        <Route path="promotions" element={<PromotionsList />} />
        <Route path="promotions/:id" element={<PromotionsDetail />} />
        <Route path="customers" element={<CustomersList />} />
        <Route path="customers/:id" element={<CustomersDetail />} />
        <Route path="loyalty" element={<LoyaltyScreen />} />
        <Route path="referrals" element={<ReferralsScreen />} />
        <Route path="*" element={<Navigate to={`${DISCOUNTS_BASE}/promotions`} replace />} />
      </Routes>
    </Box>
  );
};

export default DiscountsPage;
